/**
 * SmartBooker · Auto-playing 90s demo (vertical 9:16, 1080×1920)
 * For: dentists + physio/postural wellness prospects
 * No background music (per user's request — they'll add their own).
 *
 * 7 scenes locked to total 90s:
 *  0-08s   HERO
 *  08-22s  PROBLEMA
 *  22-50s  SMART RECORDS  ← star feature
 *  50-65s  AGENDA + BOOKING
 *  65-78s  WHATSAPP AI + REMINDER
 *  78-86s  RISULTATI
 *  86-90s  CTA
 *
 * The page can be screen-recorded directly OR rendered to MP4 via
 * the /api/video-demo/render backend endpoint (Playwright headless).
 */
import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Sparkles, Crown, Calendar, MessageSquare, Bell, Clock, Stethoscope, Activity,
  Phone, Check, AlertCircle, TrendingUp, Heart, Pill, ClipboardList, Lightbulb,
  Shield, Users, ArrowRight, Star, Download, Mic,
} from "lucide-react";

const TOTAL_MS = 90_000;

// Each subtitle line: { ms_in, ms_out, text, emphasis? }
const SUBS = [
  // Scene 1 — HERO (0-8s)
  { in: 600,  out: 4200,  text: "SmartBooker. Il sistema operativo per il tuo studio." },
  { in: 4400, out: 7800,  text: "Pensato per dentisti, fisioterapisti e osteopati." },

  // Scene 2 — PROBLEMA (8-22s)
  { in: 8400,  out: 12200, text: "Ogni mese perdi clienti, ore al telefono e fatturato per i no-show." },
  { in: 12400, out: 15800, text: "Le schede dei pazienti? Quaderni, fogli sparsi, niente storico." },
  { in: 16200, out: 21500, text: "Servirebbe qualcosa di più di un'agenda online." },

  // Scene 3 — SMART RECORDS (22-50s) ⭐
  { in: 22400, out: 26200, text: "Ecco le Smart Records. Il fascicolo intelligente di ogni cliente." },
  { in: 26500, out: 30200, text: "Anamnesi, allergie, terapie. Sempre a portata di click." },
  { in: 30400, out: 34800, text: "Cure effettuate, preventivi in attesa, piani di trattamento." },
  { in: 35100, out: 39500, text: "Per fisioterapia: VAS pre e post, esercizi assegnati, evoluzione del recupero." },
  { in: 39800, out: 44200, text: "E un assistente AI che legge tutto e ti suggerisce il prossimo passo." },
  { in: 44500, out: 49500, text: "GDPR-ready. Consensi tracciati. Audit log automatico." },

  // Scene 4 — AGENDA + BOOKING (50-65s)
  { in: 50400, out: 54200, text: "Agenda multi-poltrona o multi-cabina, sempre sincronizzata." },
  { in: 54500, out: 58800, text: "I tuoi pazienti prenotano online, 24 ore su 24." },
  { in: 59100, out: 64500, text: "Tu lavori, l'agenda si riempie da sola." },

  // Scene 5 — WHATSAPP + REMINDER (65-78s)
  { in: 65400, out: 69200, text: "WhatsApp AI risponde, prenota, conferma — al posto tuo." },
  { in: 69500, out: 72800, text: "Promemoria automatici 24 ore prima di ogni appuntamento." },
  { in: 73100, out: 77500, text: "Risultato: meno no-show, più studio operativo, più fatturato." },

  // Scene 6 — RISULTATI (78-86s)
  { in: 78400, out: 81800, text: "I nostri studi vedono in media meno 73% di no-show." },
  { in: 82100, out: 85800, text: "E un fatturato extra di oltre 8.500 euro al mese." },

  // Scene 7 — CTA (86-90s)
  { in: 86200, out: 89800, text: "Prova SmartBooker gratis per 14 giorni. Senza carta. Senza vincoli." },
];

const SCENES = [
  { in: 0,     out: 8000,  key: "hero" },
  { in: 8000,  out: 22000, key: "problem" },
  { in: 22000, out: 50000, key: "smart_records" },
  { in: 50000, out: 65000, key: "calendar" },
  { in: 65000, out: 78000, key: "whatsapp" },
  { in: 78000, out: 86000, key: "results" },
  { in: 86000, out: 90000, key: "cta" },
];

const useElapsed = (running) => {
  const [ms, setMs] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (!running) return;
    const start = performance.now();
    const tick = (t) => {
      const e = t - start;
      setMs(e);
      if (e < TOTAL_MS) ref.current = requestAnimationFrame(tick);
      else setMs(TOTAL_MS);
    };
    ref.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(ref.current);
  }, [running]);
  return ms;
};

const VideoDemoPage = () => {
  const [params] = useSearchParams();
  const autoplay = params.get("auto") === "1" || params.get("autoplay") === "1";
  const headless = params.get("headless") === "1"; // hides controls during MP4 render
  const [running, setRunning] = useState(autoplay);
  const ms = useElapsed(running);

  const activeScene = SCENES.find(s => ms >= s.in && ms < s.out) || SCENES[SCENES.length - 1];
  const sceneProgress = Math.min(1, (ms - activeScene.in) / (activeScene.out - activeScene.in));
  const activeSub = SUBS.find(s => ms >= s.in && ms < s.out);

  // Lock to vertical 9:16 viewport feel (1080×1920) — scaled to fit screen
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center overflow-hidden" data-testid="video-demo-root">
      <div
        className="relative bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 overflow-hidden shadow-2xl"
        style={{
          width: "min(100vh * 9 / 16, 1080px)",
          aspectRatio: "9 / 16",
          maxHeight: "100vh",
        }}
        data-testid="video-demo-stage"
      >
        {/* SCENE STACK */}
        <SceneHero       active={activeScene.key === "hero"}       progress={activeScene.key === "hero" ? sceneProgress : 0} />
        <SceneProblem    active={activeScene.key === "problem"}    progress={activeScene.key === "problem" ? sceneProgress : 0} />
        <SceneSmartRecs  active={activeScene.key === "smart_records"} progress={activeScene.key === "smart_records" ? sceneProgress : 0} ms={ms} />
        <SceneCalendar   active={activeScene.key === "calendar"}   progress={activeScene.key === "calendar" ? sceneProgress : 0} />
        <SceneWhatsApp   active={activeScene.key === "whatsapp"}   progress={activeScene.key === "whatsapp" ? sceneProgress : 0} />
        <SceneResults    active={activeScene.key === "results"}    progress={activeScene.key === "results" ? sceneProgress : 0} />
        <SceneCta        active={activeScene.key === "cta"}        progress={activeScene.key === "cta" ? sceneProgress : 0} />

        {/* TOP BRAND */}
        <div className="absolute top-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-30 pointer-events-none">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center shadow-lg">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="text-white font-bold tracking-tight text-sm">SmartBooker</span>
        </div>

        {/* PROGRESS BAR */}
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/5 z-30">
          <div className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400 transition-[width] duration-100"
               style={{ width: `${(ms / TOTAL_MS) * 100}%` }} />
        </div>

        {/* SUBTITLES — bottom, Apple keynote style. Hidden during headless
            MP4 capture so the user's voice-over has the spotlight. */}
        {!headless && (
          <div className="absolute left-0 right-0 bottom-[14%] flex justify-center px-8 z-30 pointer-events-none" data-testid="video-demo-subs-container">
            {activeSub && (
              <div
                key={activeSub.in}
                className="max-w-[88%] text-center text-white text-[22px] leading-[1.35] font-semibold tracking-tight px-5 py-3 rounded-2xl bg-black/60 backdrop-blur-md border border-white/10 animate-subtitleIn shadow-2xl"
                style={{ textShadow: "0 2px 12px rgba(0,0,0,.55)" }}
                data-testid="video-demo-subtitle"
              >
                {activeSub.text}
              </div>
            )}
          </div>
        )}

        {/* CONTROLS — hidden during headless MP4 capture */}
        {!headless && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-2" data-testid="video-demo-controls">
            <div className="flex items-center gap-2">
              {!running && ms === 0 && (
                <button
                  onClick={() => setRunning(true)}
                  data-testid="video-demo-play"
                  className="rounded-full px-5 py-2.5 bg-white text-slate-900 font-bold text-sm shadow-2xl hover:bg-slate-100 transition-colors"
                >
                  ▶  Riproduci 90s
                </button>
              )}
              {running && ms < TOTAL_MS && (
                <span className="rounded-full px-3 py-1 bg-black/60 backdrop-blur text-white text-[10px] font-mono">
                  {(ms / 1000).toFixed(1)}s / 90s
                </span>
              )}
            </div>
            {/* Download is always available — no need to wait the full 90s */}
            <DownloadButton />
          </div>
        )}
      </div>
    </div>
  );
};

/* ============================================================
 * SCENES
 * ============================================================ */

const Stage = ({ active, children, className = "" }) => (
  <div
    className={`absolute inset-0 transition-opacity duration-700 ${active ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"} ${className}`}
  >
    {children}
  </div>
);

/* SCENE 1 — HERO */
const SceneHero = ({ active, progress }) => (
  <Stage active={active} className="bg-[radial-gradient(ellipse_at_top,_rgba(16,185,129,.18),transparent_55%),radial-gradient(ellipse_at_bottom,_rgba(6,182,212,.16),transparent_55%)]">
    <div className="absolute inset-0 flex flex-col items-center justify-center px-10 text-center">
      <div className={`transition-all duration-700 ${progress > 0.05 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/80 text-[10px] font-bold uppercase tracking-[0.18em]">
          <Sparkles className="w-3 h-3 text-emerald-400" /> Business OS · Premium
        </span>
      </div>
      <h1 className={`text-white font-black mt-6 leading-[1.05] transition-all duration-700 delay-200 ${progress > 0.15 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
          style={{ fontSize: "min(8.5vh, 56px)" }}>
        SmartBooker
      </h1>
      <p className={`text-white/70 mt-4 max-w-md text-lg leading-snug transition-all duration-700 delay-500 ${progress > 0.32 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        Il sistema operativo per dentisti, fisioterapisti e osteopati.
      </p>
      <div className={`mt-12 grid grid-cols-2 gap-3 transition-all duration-700 delay-700 ${progress > 0.55 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        {[
          { icon: Stethoscope, label: "Dentisti" },
          { icon: Activity, label: "Fisio · Osteo" },
        ].map((c) => (
          <div key={c.label} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur px-4 py-3 flex items-center gap-2">
            <c.icon className="w-4 h-4 text-emerald-400" />
            <span className="text-white text-sm font-semibold">{c.label}</span>
          </div>
        ))}
      </div>
    </div>
  </Stage>
);

/* SCENE 2 — PROBLEMA */
const SceneProblem = ({ active, progress }) => (
  <Stage active={active} className="bg-gradient-to-br from-rose-950 via-slate-950 to-slate-950">
    <div className="absolute inset-0 flex flex-col items-center justify-center px-10">
      <div className={`text-center transition-all duration-700 ${progress > 0.05 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <h2 className="text-white font-black leading-tight" style={{ fontSize: "min(7vh, 44px)" }}>
          Quanto ti<br/>sta costando<br/>il vecchio metodo?
        </h2>
      </div>
      <div className="mt-10 space-y-3 w-full max-w-md">
        {[
          { v: "30%", l: "no-show ogni mese", delay: 0.1 },
          { v: "12h", l: "perse al telefono / settimana", delay: 0.3 },
          { v: "0",  l: "storico clienti strutturato", delay: 0.5 },
        ].map((m) => (
          <div key={m.l}
               className={`rounded-2xl bg-rose-500/10 border border-rose-500/30 px-5 py-4 flex items-center gap-4 transition-all duration-500 ${progress > m.delay ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6"}`}>
            <span className="text-3xl font-black text-rose-300 min-w-[64px]">{m.v}</span>
            <span className="text-white/80 text-sm font-medium">{m.l}</span>
            <AlertCircle className="w-4 h-4 text-rose-400 ml-auto flex-shrink-0" />
          </div>
        ))}
      </div>
    </div>
  </Stage>
);

/* SCENE 3 — SMART RECORDS (the star) */
const SceneSmartRecs = ({ active, progress, ms }) => {
  const subPhase = ms - 22000;          // 0..28000
  const showDental = subPhase < 14000;  // 0..14s dental, 14..28s fisio
  return (
    <Stage active={active} className="bg-gradient-to-br from-violet-950 via-slate-950 to-cyan-950">
      <div className="absolute inset-0 flex flex-col px-7 pt-28 pb-32">
        <div className={`text-center mb-5 transition-all duration-700 ${progress > 0.04 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-violet-500 to-cyan-500 text-white text-[10px] font-bold uppercase tracking-[0.18em] shadow-lg">
            <Crown className="w-3 h-3" /> Smart Records · Premium
          </span>
          <h2 className="text-white font-black mt-3" style={{ fontSize: "min(5.5vh, 32px)" }}>
            Ogni cliente,<br/>una scheda intelligente.
          </h2>
        </div>

        {/* Dental record card */}
        <div className={`absolute inset-x-7 top-[33%] transition-all duration-700 ${showDental && progress > 0.08 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <RecordCard
            grad="from-cyan-500 to-blue-600"
            soft="from-cyan-50/95 to-blue-50/95"
            icon={Heart}
            badge="STUDIO DENTISTICO"
            name="Alessandra Bruno"
            sub="Paziente · 3 anni"
            stats={[["€570", "speso"], ["6", "cure"], ["17 apr", "ultima"]]}
            fields={[
              ["Allergie", "Penicillina", "alert"],
              ["Patologie", "Diabete tipo 2", null],
              ["Fumatrice", "Sì", "alert"],
            ]}
            rows={[
              { i: Pill, t: "Otturazione · 4.6", s: "completata · Dr. Sorriso" },
              { i: Stethoscope, t: "Igiene professionale", s: "completata · Dr.ssa Bianchi" },
              { i: ClipboardList, t: "Preventivo €1.420", s: "in attesa · 12 giorni" },
            ]}
            ai={{
              alert: "Allergia penicillina · Igiene da rinnovare",
              suggestion: "Recall WhatsApp + screening parodontale",
            }}
          />
        </div>

        {/* Physio record card */}
        <div className={`absolute inset-x-7 top-[33%] transition-all duration-700 ${!showDental ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <RecordCard
            grad="from-emerald-500 to-teal-600"
            soft="from-emerald-50/95 to-teal-50/95"
            icon={Activity}
            badge="STUDIO FISIO · OSTEO"
            name="Marco Verdi"
            sub="Paziente · 5° seduta su 10"
            stats={[["3/10 ↓", "VAS"], ["5/10", "sedute"], ["€275", "spesa"]]}
            fields={[
              ["Zona dolore", "Lombare L4-L5", null],
              ["VAS iniziale", "8/10", "alert"],
              ["VAS attuale", "3/10 ↓", "ok"],
            ]}
            rows={[
              { i: Pill, t: "Tecar + manipolazione L4-L5", s: "VAS 5→3 · migliorato" },
              { i: ClipboardList, t: "Esercizi McKenzie + core", s: "domiciliari · 4×sett." },
              { i: TrendingUp, t: "Programma in netto miglioramento", s: "stimate 3-4 sedute alla dimissione" },
            ]}
            ai={{
              alert: "Aderenza esercizi alta · postura da rivedere",
              suggestion: "Pianificare rieducazione ergonomica + dimissione",
            }}
          />
        </div>
      </div>
    </Stage>
  );
};

const RecordCard = ({ grad, soft, icon: Icon, badge, name, sub, stats, fields, rows, ai }) => {
  const initials = name.split(" ").map(p => p[0]).join("").slice(0, 2);
  return (
    <div className={`rounded-3xl bg-gradient-to-br ${soft} border border-white/30 p-4 shadow-2xl backdrop-blur`}>
      {/* header */}
      <div className="flex items-center gap-3 pb-3 border-b border-slate-200/70">
        <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${grad} text-white flex items-center justify-center font-black text-base shadow`}>
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-black text-slate-900 truncate text-base">{name}</p>
          <p className="text-[11px] text-slate-500">{sub}</p>
          <div className="mt-1 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white text-slate-700 shadow-sm">
            <Icon className="w-2.5 h-2.5" /> {badge}
          </div>
        </div>
      </div>
      {/* stats */}
      <div className="grid grid-cols-3 gap-2 mt-3">
        {stats.map(([v, l], i) => (
          <div key={i} className="rounded-xl bg-white/80 px-2 py-1.5 text-center">
            <p className="text-[14px] font-black text-slate-900 leading-tight">{v}</p>
            <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">{l}</p>
          </div>
        ))}
      </div>
      {/* fields */}
      <div className="mt-3 grid grid-cols-1 gap-1">
        {fields.map(([l, v, k], i) => (
          <div key={i} className={`flex items-center justify-between text-[12px] px-2 py-1 rounded-md ${k === "alert" ? "bg-amber-100/70" : k === "ok" ? "bg-emerald-100/70" : "bg-white/60"}`}>
            <span className="text-slate-500">{l}</span>
            <span className={`font-bold ${k === "alert" ? "text-amber-800" : k === "ok" ? "text-emerald-800" : "text-slate-800"}`}>{v}</span>
          </div>
        ))}
      </div>
      {/* rows */}
      <ul className="mt-3 space-y-1.5">
        {rows.map((r, i) => (
          <li key={i} className="flex items-center gap-2 p-2 rounded-xl bg-white/80 border border-slate-100">
            <span className={`w-7 h-7 rounded-lg bg-gradient-to-br ${grad} text-white flex items-center justify-center flex-shrink-0`}>
              <r.i className="w-3.5 h-3.5" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold text-slate-900 truncate">{r.t}</p>
              <p className="text-[10px] text-slate-500 truncate">{r.s}</p>
            </div>
          </li>
        ))}
      </ul>
      {/* AI box */}
      <div className="mt-3 rounded-xl bg-gradient-to-r from-violet-100/80 to-cyan-100/80 border border-violet-200 p-2.5 flex items-start gap-2">
        <Sparkles className="w-3.5 h-3.5 text-violet-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-[10px] font-bold text-violet-700 uppercase tracking-wider">AI · Claude Sonnet</p>
          <p className="text-[11px] text-slate-700 leading-snug mt-0.5">⚠ {ai.alert}</p>
          <p className="text-[11px] text-slate-700 leading-snug mt-0.5">→ {ai.suggestion}</p>
        </div>
      </div>
    </div>
  );
};

/* SCENE 4 — CALENDAR */
const SceneCalendar = ({ active, progress }) => (
  <Stage active={active} className="bg-gradient-to-br from-cyan-950 via-slate-950 to-emerald-950">
    <div className="absolute inset-0 flex flex-col items-center justify-center px-8">
      <h2 className={`text-white font-black text-center transition-all duration-700 ${progress > 0.06 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
          style={{ fontSize: "min(6vh, 36px)" }}>
        L'agenda<br/>che si riempie da sola.
      </h2>
      <div className={`mt-7 w-full max-w-md rounded-3xl border border-white/10 bg-white/95 p-4 shadow-2xl transition-all duration-700 delay-200 ${progress > 0.15 ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-bold text-slate-900">Lunedì 12 maggio</p>
          <Calendar className="w-4 h-4 text-emerald-500" />
        </div>
        <div className="space-y-1.5">
          {[
            { t: "09:00", c: "Otturazione · A. Bruno", g: "from-cyan-500 to-blue-500", delay: 0.18 },
            { t: "10:30", c: "Igiene · L. Conti",      g: "from-emerald-500 to-teal-500", delay: 0.30 },
            { t: "12:00", c: "PRENOTAZIONE NUOVA",     g: "from-fuchsia-500 to-pink-500", delay: 0.45, blink: true },
            { t: "14:00", c: "Visita · M. Verdi",      g: "from-violet-500 to-purple-500", delay: 0.55 },
            { t: "15:30", c: "Tecar · S. Riva",        g: "from-orange-500 to-rose-500", delay: 0.65 },
            { t: "17:00", c: "Recall · F. Greco",      g: "from-sky-500 to-cyan-500", delay: 0.75 },
          ].map(s => (
            <div key={s.t}
                 className={`flex items-center gap-3 p-2 rounded-xl bg-slate-50 transition-all duration-500 ${progress > s.delay ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"}`}>
              <span className="text-[11px] font-mono text-slate-500 w-10">{s.t}</span>
              <div className={`flex-1 h-7 rounded-md bg-gradient-to-r ${s.g} flex items-center px-2.5 ${s.blink ? "animate-pulse" : ""}`}>
                <span className="text-[10px] font-bold text-white truncate">{s.c}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </Stage>
);

/* SCENE 5 — WHATSAPP */
const SceneWhatsApp = ({ active, progress }) => (
  <Stage active={active} className="bg-gradient-to-br from-emerald-950 via-slate-950 to-slate-950">
    <div className="absolute inset-0 flex flex-col items-center justify-center px-10">
      <h2 className={`text-white font-black text-center transition-all duration-700 ${progress > 0.06 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
          style={{ fontSize: "min(6vh, 36px)" }}>
        WhatsApp AI<br/>al posto tuo.
      </h2>
      <div className="mt-6 w-full max-w-sm rounded-3xl bg-[#0b141a] p-3 border border-emerald-500/30 shadow-2xl">
        <div className="flex items-center gap-2 pb-2 border-b border-white/10 mb-2">
          <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center">
            <MessageSquare className="w-3.5 h-3.5 text-white" />
          </div>
          <p className="text-white text-sm font-bold">Studio Sorriso</p>
          <span className="ml-auto text-[10px] text-emerald-300 font-bold">online</span>
        </div>
        <div className="space-y-2">
          {[
            { from: "in", t: "Ciao, vorrei prenotare un controllo", delay: 0.15 },
            { from: "out", t: "Ciao Marco! Disponibilità:\n• Domani 10:00\n• Domani 15:00\n• Mercoledì 11:00", delay: 0.30 },
            { from: "in", t: "Domani 10", delay: 0.50 },
            { from: "out", t: "✓ Confermato! Riceverai un promemoria 24h prima.", delay: 0.62 },
            { from: "sys", t: "🔔 Reminder inviato · Lunedì ore 10:00", delay: 0.78 },
          ].map((b, i) => (
            <div key={i}
                 className={`flex transition-all duration-400 ${progress > b.delay ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"} ${b.from === "in" ? "justify-start" : b.from === "sys" ? "justify-center" : "justify-end"}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-[12px] leading-snug whitespace-pre-line ${
                b.from === "in"  ? "bg-white text-slate-900" :
                b.from === "out" ? "bg-emerald-600 text-white" :
                "bg-amber-500/20 text-amber-200 border border-amber-500/30 text-[10px] font-bold"
              }`}>
                {b.t}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </Stage>
);

/* SCENE 6 — RISULTATI */
const SceneResults = ({ active, progress }) => (
  <Stage active={active} className="bg-gradient-to-br from-emerald-900 via-slate-950 to-slate-950">
    <div className="absolute inset-0 flex flex-col items-center justify-center px-10">
      <h2 className={`text-white font-black text-center transition-all duration-700 ${progress > 0.06 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
          style={{ fontSize: "min(6vh, 36px)" }}>
        I risultati<br/>parlano da soli.
      </h2>
      <div className="mt-9 grid grid-cols-1 gap-4 w-full max-w-md">
        {[
          { v: "−73%", l: "no-show in 30 giorni", grad: "from-emerald-400 to-teal-500", delay: 0.10 },
          { v: "+€8.500", l: "fatturato extra al mese", grad: "from-cyan-400 to-blue-500", delay: 0.30 },
          { v: "12h", l: "salvate ogni settimana", grad: "from-violet-400 to-purple-500", delay: 0.50 },
        ].map(m => (
          <div key={m.l}
               className={`rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-5 flex items-center gap-4 transition-all duration-700 ${progress > m.delay ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <span className={`text-4xl font-black bg-gradient-to-r ${m.grad} bg-clip-text text-transparent leading-none min-w-[110px]`}>{m.v}</span>
            <span className="text-white/80 text-sm font-medium leading-snug">{m.l}</span>
          </div>
        ))}
      </div>
    </div>
  </Stage>
);

/* SCENE 7 — CTA */
const SceneCta = ({ active, progress }) => (
  <Stage active={active} className="bg-[radial-gradient(ellipse_at_center,_rgba(16,185,129,.25),transparent_60%),linear-gradient(180deg,#020617,#020617)]">
    <div className="absolute inset-0 flex flex-col items-center justify-center px-10 text-center">
      <Sparkles className={`w-9 h-9 text-emerald-400 transition-all duration-700 ${progress > 0.05 ? "opacity-100 scale-100" : "opacity-0 scale-50"}`} />
      <h2 className={`text-white font-black mt-4 leading-tight transition-all duration-700 delay-100 ${progress > 0.15 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
          style={{ fontSize: "min(7vh, 44px)" }}>
        Prova SmartBooker<br/>gratis 14 giorni.
      </h2>
      <p className={`text-white/70 mt-3 text-sm transition-all duration-700 delay-300 ${progress > 0.4 ? "opacity-100" : "opacity-0"}`}>
        Senza carta di credito. Senza vincoli.
      </p>
      <div className={`mt-8 transition-all duration-700 delay-500 ${progress > 0.55 ? "opacity-100 scale-100" : "opacity-0 scale-90"}`}>
        <span className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-emerald-400 to-cyan-500 text-slate-950 font-black text-base shadow-2xl">
          smartbooker.it
          <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </div>
  </Stage>
);

/* DOWNLOAD MP4 — static asset (works on prod without Playwright/ffmpeg) */
const DownloadButton = () => (
  <div className="flex flex-col items-center gap-2">
    <a
      href="/smartbooker-demo-90s.mp4"
      download="smartbooker-demo-90s.mp4"
      data-testid="video-demo-download"
      className="rounded-full px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs flex items-center gap-1.5 shadow-2xl"
    >
      <Download className="w-3.5 h-3.5" />
      Scarica MP4 (90s · 9:16)
    </a>
    <a
      href="/video-demo/voce"
      data-testid="video-demo-add-voice"
      className="rounded-full px-3 py-1 bg-white/10 hover:bg-white/20 text-white font-semibold text-[10px] flex items-center gap-1 border border-white/15 transition-colors"
    >
      <Mic className="w-3 h-3" />
      Aggiungi la tua voce
    </a>
  </div>
);

export default VideoDemoPage;
